import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'
import { IGNORE_BEFORE, RECENT_RUNS, easternDate, syncStatus, worst, type SyncRun } from '../lib/syncstatus'
import { explainState, pingExtension, runSyncNow, type ExtensionState } from '../lib/extension'

/**
 * One line at the top of the roster: when the roster last updated, and a button
 * to update it now.
 *
 * WHY THIS EXISTS AT ALL
 *
 * The morning pull happens inside Renee's browser, on whichever of three
 * machines happens to be open. There is no server that can notice it stopped —
 * that is the whole constraint the sync is built around. So the only thing that
 * can tell her it has stopped is the app, and the only evidence it has is
 * `sync_runs`.
 *
 * ALWAYS MOUNTED, SHOWN ONLY ON THE ROSTER TABS
 *
 * `visible` hides the markup rather than the parent unmounting the component,
 * and that is not tidiness. A pull takes up to three minutes. Press Update,
 * glance at the Outbox, come back: with a conditional mount the "Updating…"
 * state, the result message and the in-flight promise all vanish, the button is
 * live again, and pressing it starts a second sync or earns a "That just ran"
 * she has no context for.
 *
 * NOTHING HERE IS URGENT WHEN IT IS FINE. The quiet state is one grey sentence
 * with a button beside it; only amber and red raise their voice.
 */

/** The only source with a sync today. reZEN joins this in phase 2. */
const KIND = 'daily_metabase'
const LABEL = 'The roster'

/**
 * How often the strip re-reads, while the tab is actually being looked at.
 *
 * A dashboard is left open overnight. Read once at mount and the next morning
 * it greets her with yesterday's rows AND yesterday's idea of what day it is —
 * "The roster updated this morning", about a morning that has been and gone.
 */
const REFRESH_MS = 5 * 60_000

/** After Metabase is opened for sign-in, the run finishes on an alarm. */
const SIGNIN_POLL_MS = 15_000
const SIGNIN_POLL_FOR_MS = 6 * 60_000

export default function SyncStrip({ visible }: { visible: boolean }) {
  const [runs, setRuns] = useState<SyncRun[] | null>(null)
  const [readError, setReadError] = useState<string | null>(null)
  const [ext, setExt] = useState<ExtensionState | null>(null)
  const [busy, setBusy] = useState(false)
  const [said, setSaid] = useState<string | null>(null)
  /**
   * Bumped, not toggled. A boolean set true while already true is a no-op in
   * React, so pressing Update again five minutes into a six-minute watch did
   * not restart the watch — it expired ten seconds later, having just promised
   * her it would finish by itself. A counter always changes.
   */
  const [watch, setWatch] = useState(0)
  /** Bumped on a timer and on refocus, so `today` and the age are never stale. */
  const [tick, setTick] = useState(0)

  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])

  const today = easternDate()

  const load = useCallback(async () => {
    /**
     * `stats` IS IN HERE, and leaving it out made `healthOf` dead code.
     *
     * `syncStatus` and `watchdogVerdict` read `run.stats` to notice a run that
     * SUCCEEDED and is quietly wrong — a Metabase card that stopped arriving, or
     * a baseline that was never claimed. Neither column list carried it, so both
     * functions saw `stats: undefined` on every row and answered "all is well"
     * for ever, which is the exact failure this file's own comments describe:
     * a shared function handed different rows gives different answers.
     *
     * The two lists must stay identical. `test-syncstatus.mjs` reads both out of
     * the source and compares them, so they cannot drift apart or quietly lose a
     * column again.
     */
    const columns = 'id,kind,run_date,result,reason,message,stats,started_at,finished_at'
    const recent = await supabase
      .from('sync_runs')
      .select(columns)
      .eq('kind', KIND)
      /**
       * Filtered in the query as well as in `usableRuns`, so the seeding row
       * cannot arrive at all. Belt and braces on purpose: this is the one row
       * whose presence produces a *reassuring* lie, and a reassuring lie is
       * never noticed.
       */
      .gte('run_date', IGNORE_BEFORE)
      // `id` breaks the tie so the `limit` boundary is a definite row rather
      // than whatever the read happened to return first — and so the watchdog
      // endpoint, which orders identically, sees the same window.
      .order('run_date', { ascending: false })
      .order('id', { ascending: false })
      .limit(RECENT_RUNS)
    if (!alive.current) return
    if (recent.error) return setReadError(recent.error.message)

    /**
     * The last success, fetched separately, and this is not belt-and-braces.
     *
     * A window of recent rows is exactly the wrong shape for the question "when
     * did it last WORK". A sustained outage fills the table with failures —
     * one per reason per day per machine, three machines — so after a couple of
     * bad weeks the last genuine success scrolls out of the window, and the
     * strip switches from the true and actionable "has not updated since
     * 4 August — 10 working days ago" to the flatly false "has never updated
     * successfully". Wrong in precisely the scenario the feature exists for.
     */
    const success = await supabase
      .from('sync_runs')
      .select(columns)
      .eq('kind', KIND)
      .eq('result', 'ok')
      .gte('run_date', IGNORE_BEFORE)
      .order('run_date', { ascending: false })
      .order('id', { ascending: false })
      .limit(1)
    if (!alive.current) return
    if (success.error) return setReadError(success.error.message)

    const rows = [...((recent.data ?? []) as SyncRun[])]
    for (const row of (success.data ?? []) as SyncRun[]) {
      if (!rows.some((r) => r.id === row.id)) rows.push(row)
    }
    setReadError(null)
    setRuns(rows)
  }, [])

  const check = useCallback(async () => {
    const state = await pingExtension()
    if (alive.current) setExt(state)
  }, [])

  useEffect(() => {
    void load()
    void check()
    /**
     * The result of the last press does not survive a refresh, and must not.
     * `said` was cleared only by the NEXT press, so a tab left open from Monday
     * showed amber "has not updated since Monday — 2 working days ago" beside
     * "Updated. No new agents today." — two contradictory sentences on one
     * line, which is the exact failure the `in-progress` state was split out to
     * avoid.
     */
    if (tick > 0) setSaid(null)
  }, [load, check, tick])

  useEffect(() => {
    /**
     * The extension announces itself with an unsolicited `hello` at
     * `document_start`, which is before React has mounted and attached any
     * listener — so the ping is what actually establishes the state. This
     * catches the case where the extension is installed or enabled while the
     * page is already open.
     */
    const onHello = (event: MessageEvent) => {
      if (event.source !== window || event.origin !== window.location.origin) return
      const d = event.data as { source?: string; type?: string } | null
      if (d?.source === 'agentcare-extension' && d.type === 'hello') void check()
    }
    /**
     * Coming back to the tab is the moment worth re-reading on. A run that
     * finished while she was in Gmail is exactly what she wants to see, and
     * polling a table every few seconds to catch it would be rude to a database
     * with one user.
     */
    /**
     * `focus` and `visibilitychange` both fire on a tab return, in separate
     * tasks, so React does not batch them — every switch cost two reads of a
     * database this file is otherwise careful to be polite to.
     */
    let last = 0
    const onWake = () => {
      if (document.visibilityState !== 'visible') return
      const now = Date.now()
      if (now - last < 1000) return
      last = now
      setTick((t) => t + 1)
    }
    const timer = setInterval(onWake, REFRESH_MS)
    window.addEventListener('message', onHello)
    window.addEventListener('focus', onWake)
    document.addEventListener('visibilitychange', onWake)
    return () => {
      clearInterval(timer)
      window.removeEventListener('message', onHello)
      window.removeEventListener('focus', onWake)
      document.removeEventListener('visibilitychange', onWake)
    }
  }, [check])

  /**
   * Watch for a sign-in run finishing.
   *
   * Chris chose the automatic flow: the extension opens Metabase, she signs in,
   * and an alarm finishes the pull minutes later. The screen promised her it
   * would "finish by itself" and then never changed, because nothing re-read
   * the table. This is the part that makes the promise true.
   */
  useEffect(() => {
    if (watch === 0) return
    const started = Date.now()
    const timer = setInterval(() => {
      if (Date.now() - started > SIGNIN_POLL_FOR_MS) return clearInterval(timer)
      void load()
      void check()
    }, SIGNIN_POLL_MS)
    return () => clearInterval(timer)
  }, [watch, load, check])

  /**
   * Stop watching the moment the day is covered. A run that finishes in thirty
   * seconds otherwise costs another twenty round trips to a database with one
   * user, which is the thing the refresh interval is deliberately polite about.
   */
  const succeededToday = (runs ?? []).some((r) => r.result === 'ok' && r.run_date === today)
  useEffect(() => {
    if (watch !== 0 && succeededToday) setWatch(0)
  }, [watch, succeededToday])

  async function update() {
    if (busy) return
    setBusy(true)
    setSaid(null)
    try {
      const r = await runSyncNow()
      if (!alive.current) return
      switch (r.state) {
        case 'missing':
        case 'stale':
          /**
           * No `said` here. The sentence is already rendered from `ext` where
           * the button would otherwise be, and setting it as well printed the
           * whole paragraph twice in a row on one line.
           */
          setExt({ state: r.state })
          return
        case 'timeout':
          setSaid(
            'The update is taking longer than usual. It may still be running — leave it a few minutes and this line will catch up on its own.',
          )
          return
        case 'signing-in':
          setSaid(r.message)
          setWatch((w) => w + 1)
          return
        case 'in-progress':
          setSaid('An update is already running. This line will catch up when it finishes.')
          setWatch((w) => w + 1)
          return
        case 'already-done':
          /**
           * NOT "another computer". `already-done` means only that a successful
           * run exists for today — and the commonest writer of that row is
           * THIS machine's own 7am alarm. Saying another computer did it sent
           * Chris looking for a second installation that does not exist.
           */
          setSaid("The roster had already been updated today, so there was nothing to do.")
          break
        case 'refused':
          setSaid(r.message)
          break
        case 'done':
          setSaid(
            r.created === 0
              ? 'Updated. No new agents today.'
              : `Updated. ${r.created} new ${r.created === 1 ? 'agent' : 'agents'} added${
                  r.enrolled > 0
                    ? `, ${r.enrolled} put into a campaign.`
                    : '. Nobody was put into a campaign.'
                }`,
          )
          break
      }
      await load()
    } finally {
      // In a `finally` because a throw from `postMessage` used to leave the
      // button reading "Updating…" and disabled for the life of the page.
      if (alive.current) setBusy(false)
    }
  }

  if (!visible) return null

  // Nothing is claimed until the runs have actually been read. A strip that
  // says "never updated" for half a second on every page load is a strip that
  // gets ignored the day it means it.
  if (runs === null && !readError) return null

  const status = syncStatus(runs ?? [], KIND, today, LABEL)
  const extNote = ext && ext.state !== 'ready' ? explainState(ext) : null
  const signingIn = ext?.state === 'ready' && ext.signingIn
  /**
   * The extension's own record of its last attempt, used only when the table
   * has nothing to say. The failure it covers is the one that leaves no row at
   * all: the pull worked, the post to the dashboard did not, so there is
   * nothing in `sync_runs` and the reason exists only inside the extension.
   */
  const fromExtension =
    !status.detail && ext?.state === 'ready' && ext.last && ext.last.ok === false && ext.last.message
      ? ext.last.message
      : null

  const detail = readError
    ? `The last-updated check could not read its own records: ${readError}`
    : (status.detail ?? fromExtension)

  /**
   * A failure the extension knows about and the table does not still has to
   * colour the strip.
   *
   * `syncStatus` can only reason about rows. The failure with no row is the one
   * where the pull worked and the post to the dashboard did not — and
   * `reportFailure` goes through the same dead connection, so it cannot record
   * anything either. Left on `status.tone` alone that renders calm blue,
   * headline "The roster updated yesterday", reason in grey: true, reassuring,
   * and about the wrong day. Which is the defect this whole tone rule exists
   * to remove, reached through the other evidence channel.
   */
  const tone = readError ? 'warn' : fromExtension ? worst(status.tone, 'warn') : status.tone

  return (
    <div className={`syncstrip ${tone}`} role="status" aria-live="polite">
      <span className="what">
        {readError ? `${LABEL} — could not check when it last updated.` : status.headline}
      </span>
      {/*
        The exact moment, beside the sentence rather than inside it.

        Separate from the headline on purpose. `syncStatus` builds a sentence
        that is complete and punctuated on its own, and splicing a timestamp
        into it would mean the pure function producing two different sentences
        depending on whether a column happened to be filled in. Here, a missing
        stamp simply renders nothing and the line still reads properly.

        Hidden when the read failed, because in that state the headline is about
        not being able to check — and a precise time under it would be
        describing a fact we have just said we could not establish.
      */}
      {!readError && status.stamp && <span className="when">{status.stamp}</span>}
      {detail && <span className="why">{detail}</span>}
      <span className="grow" />
      {said && <span className="why">{said}</span>}
      {/*
        Not a greyed-out button that looks pressable and does nothing. When this
        laptop cannot run the update it says so in a sentence and there is no
        button at all — nothing to press, and nothing to work out from the
        absence of a response.
      */}
      {ext === null ? (
        <span className="why">Checking…</span>
      ) : ext.state === 'ready' ? (
        <button className="btn sm" disabled={busy || signingIn} onClick={() => void update()}>
          {busy ? 'Updating…' : signingIn ? 'Waiting for sign-in…' : 'Update now'}
        </button>
      ) : (
        <span className="why">{extNote}</span>
      )}
    </div>
  )
}
